import React, { memo, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Info } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { BlurFade } from "../magicui/blur-fade";

const LogoReleaseSection: React.FC = () => {
  const { theme } = useTheme();
  
  // Memoize logo meanings to prevent recreation on every render
  const logoMeanings = useMemo(() => [
    {
      element: 'Bukit',
      description: 'Melambangkan karakteristik geografis desa yang berada di kawasan pegunungan, mencerminkan ketinggian dan stabilitas.'
    },
    {
      element: 'Aliran Air',
      description: 'Merepresentasikan sumber daya air yang melimpah dari sungai terdekat, simbol kehidupan dan keberlangsungan.'
    },
    {
      element: 'Tangan Bergandeng',
      description: 'Menggambarkan pemberdayaan masyarakat dan kolaborasi. Warna oranye melambangkan harmonisasi antara berbagai elemen.'
    },
    {
      element: 'Figur Manusia',
      description: 'Merepresentasikan mahasiswa KKN sebagai agen perubahan dan pembangunan dalam masyarakat desa.'
    },
    {
      element: 'Tanaman Padi',
      description: 'Menunjukkan potensi pertanian desa dengan hamparan sawah sebagai sumber ekonomi utama masyarakat.'
    }
  ], []);

  // Memoize logo source based on theme
  const logoSrc = useMemo(() => 
    theme === 'dark' ? "/assets/logo/logo_kkn_putih.png" : "/assets/logo/logo_kkn_hitam.png"
  , [theme]);

  // Optimize error handler with useCallback
  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    const parent = target.parentElement;
    
    if (parent) {
      target.style.display = 'none';
      parent.innerHTML = `
        <div class="text-center text-gray-600 dark:text-gray-400">
          <div class="text-6xl mb-4">🏔️</div>
          <div class="text-lg font-bold">KKN UNSOED 2025</div>
          <div class="text-sm">DESA KARANGMALANG</div>
        </div>
      `;
    }
  }, []);

  return (
    <BlurFade>
    <section className="py-16 lg:py-24 bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Simplified Header - Single fade in */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent mb-8 leading-tight py-2">
            Logo Release
          </h2>
          <div className="h-1 bg-gradient-to-r from-emerald-400 to-orange-500 rounded-full mx-auto mb-8 w-32 sm:w-48" />
        </motion.div>

        {/* Simplified Logo Display - Static glow */}
        <div className="text-center mb-16">
          <div className="relative mx-auto w-64 h-64 sm:w-72 sm:h-72 lg:w-80 lg:h-80">
            {/* Static glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/10 via-blue-500/10 to-orange-400/10 rounded-full blur-xl" />
            
            {/* Outer Ring */}
            <div className="absolute inset-2 bg-gradient-to-r from-emerald-400 to-orange-400 rounded-full p-1">
              {/* Main Logo Container */}
              <div className="relative w-full h-full bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-xl">
                <img 
                  src={logoSrc}
                  alt="Logo KKN Desa Karangmalang 2025" 
                  className="w-4/5 h-4/5 object-contain"
                  loading="lazy"
                  onError={handleImageError}
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Simplified Philosophy Section */}
        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-3xl p-8 lg:p-12 shadow-xl border border-gray-200/50 dark:border-slate-700/50">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-8">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <Info className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white">
              Filosofi Logo
            </h3>
          </div>
          
          {/* Static meanings grid - no individual animations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {logoMeanings.map((meaning, index) => (
              <div
                key={meaning.element}
                className="group flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors duration-200"
              >
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-emerald-500 to-orange-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-2 text-lg">
                    {meaning.element}
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm sm:text-base">
                    {meaning.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
    </BlurFade>
  );
};

// Memoize the entire component to prevent unnecessary re-renders
export default memo(LogoReleaseSection);